// src/pages/communication/CommunicationPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ChatThreadList from '../../components/communication/ChatThreadList';
import ChatMessages from '../../components/communication/ChatMessages';
import BroadcastForm from '../../components/communication/BroadcastForm';
import Button from '../../components/common/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { 
  getChatThreads, 
  getChatMessages, 
  sendMessage, 
  markThreadAsRead,
  sendBroadcastMessage
} from '../../services/communicationService';

const CommunicationPage = () => {
  const { vendorProfile } = useAuth();
  const { showAlert } = useNotification();
  
  // State
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState('');
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [messageInput, setMessageInput] = useState('');
  const [showBroadcast, setShowBroadcast] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  
  // Load threads
  const loadThreads = async (refresh = false) => {
    try {
      if (!vendorProfile?.uid) return;
      
      setLoading(true);
      setError('');
      
      // Get threads
      const options = { lastVisible: refresh ? null : lastVisible };
      const result = await getChatThreads(vendorProfile.uid, options);
      
      // Update state
      if (refresh) {
        setThreads(result.threads);
      } else {
        setThreads(prev => [...prev, ...result.threads]);
      }
      
      setLastVisible(result.lastDoc);
      setHasMore(result.threads.length === 20); // 20 is the pageSize in the service
      setLoading(false);
    } catch (error) {
      console.error('Error loading chat threads:', error);
      setError('Failed to load chat threads. Please try again.');
      setLoading(false);
    }
  };
  
  // Load messages for active thread
  const loadMessages = async () => {
    try {
      if (!activeThread) return;
      
      setLoadingMessages(true);
      
      // Get messages
      const result = await getChatMessages(activeThread.id);
      setMessages(result.messages);
      
      // Mark thread as read
      await markThreadAsRead(activeThread.id, 'vendor');
      
      // Update thread in state to show as read
      setThreads(prev => 
        prev.map(thread => 
          thread.id === activeThread.id 
            ? { ...thread, unreadVendor: false } 
            : thread
        )
      );
      
      setLoadingMessages(false);
      
      // Scroll to bottom
      scrollToBottom();
    } catch (error) {
      console.error('Error loading messages:', error);
      setLoadingMessages(false);
    }
  };
  
  // Initial load
  useEffect(() => {
    if (vendorProfile?.uid) {
      loadThreads(true);
    }
  }, [vendorProfile]);
  
  // Load messages when active thread changes
  useEffect(() => {
    if (activeThread) {
      loadMessages();
    } else {
      setMessages([]);
    }
  }, [activeThread]);
  
  // Scroll to bottom of messages
  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  
  // Send a message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageInput.trim() || !activeThread) return;
    
    try {
      const message = {
        text: messageInput,
        senderId: vendorProfile.uid,
        senderType: 'vendor',
      };
      
      // Add message locally for immediate feedback
      const tempMessage = {
        ...message,
        id: 'temp-' + Date.now(),
        timestamp: new Date(),
        pending: true
      };
      
      setMessages(prev => [...prev, tempMessage]);
      setMessageInput('');
      scrollToBottom();
      
      // Send message
      const sentMessage = await sendMessage(activeThread.id, message);
      
      // Replace temp message with actual message
      setMessages(prev => 
        prev.map(msg => 
          msg.id === tempMessage.id ? { ...sentMessage, pending: false } : msg
        )
      );
      
      // Update thread in threads list
      setThreads(prev => 
        prev.map(thread => 
          thread.id === activeThread.id 
            ? { 
                ...thread, 
                lastMessage: message.text,
                lastMessageSender: message.senderId,
                lastMessageTimestamp: new Date(),
                unreadVendor: false
              } 
            : thread
        )
      );
    } catch (error) {
      console.error('Error sending message:', error);
      showAlert('Failed to send message. Please try again.', 'error');
    }
  };
  
  // Handle broadcast message
  const handleBroadcast = async (message, selectedCustomers = []) => {
    try {
      const customerIds = selectedCustomers.length > 0 
        ? selectedCustomers 
        : null; // null means all customers
      
      await sendBroadcastMessage(vendorProfile.uid, message, customerIds);
      
      showAlert('Broadcast message sent successfully', 'success');
      setShowBroadcast(false);
      
      // Refresh threads to show updated last messages
      loadThreads(true);
    } catch (error) {
      console.error('Error sending broadcast:', error);
      showAlert('Failed to send broadcast message', 'error');
    }
  };
  
  // Select a thread
  const handleThreadSelect = (thread) => {
    setActiveThread(thread);
  };
  
  // Load more threads
  const handleLoadMore = () => {
    if (hasMore && !loading) {
      loadThreads();
    }
  };
  
  return (
    <DashboardLayout>
      <div className="flex flex-col h-screen overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-900">
              Customer Communication
            </h1>
            <Button
              variant="primary"
              onClick={() => setShowBroadcast(true)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
              </svg>
              Send Broadcast
            </Button>
          </div>
        </div>
        
        {/* Chat Interface */}
        <div className="flex-1 flex overflow-hidden">
          {/* Thread List */}
          <div className="w-full md:w-1/3 border-r border-gray-200 bg-white overflow-y-auto">
            <ChatThreadList 
              threads={threads}
              activeThread={activeThread}
              onThreadSelect={handleThreadSelect}
              loading={loading}
              error={error}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
            />
          </div>
          
          {/* Messages */}
          <div className="hidden md:flex md:w-2/3 md:flex-col">
            {activeThread ? (
              <ChatMessages 
                thread={activeThread}
                messages={messages}
                loading={loadingMessages}
                messageInput={messageInput}
                setMessageInput={setMessageInput}
                onSendMessage={handleSendMessage}
                messagesEndRef={messagesEndRef}
                vendorId={vendorProfile?.uid}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center bg-gray-50 p-4">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <h3 className="mt-2 text-lg font-medium text-gray-900">Select a conversation</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Choose a conversation from the list to start chatting with a customer.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Broadcast Modal */}
      {showBroadcast && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <BroadcastForm 
                onSend={handleBroadcast}
                onCancel={() => setShowBroadcast(false)}
                threads={threads} // Used to extract customer list
              />
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default CommunicationPage;